import { map, Observable } from 'rxjs';
import { inject, Injectable } from '@angular/core';

/**
 * models
 */
import { IUploadBatchModel, UploadBatchModel } from '../models';
import { DataTableResponseModel } from '../../data-table/models/data-table-response-model';

/**
 * interfaces
 */
import { IUploadsApiService, IUploadsFacadeService } from '../interfaces';
import { IDataTableRequest } from '../../data-table/interfaces';
import { IDataTableResponseModel } from '../../data-table/models/interfaces';

/**
 * enums
 */
import { DataTableSortEnum } from '../../data-table/enums';

/**
 * consts
 */
import { UploadServiceConst } from '../consts';

/**
 * abstracts
 */
import { AbstractUploadApiService } from '../abstracts';

/**
 * services
 */
import { UPLOADS_API } from '../provider/uploads-api.token';

/**
 * What a screen showing upload batches talks to.
 *
 * It owns the two things the HTTP layer deliberately does not: the
 * default sorting of the list, newest upload first, and turning the raw
 * batches into models.
 *
 * It holds no state on purpose. A screen decides for itself what to keep
 * — which batch is expanded, which details it has already fetched —
 * because the same list is rendered differently wherever it appears.
 */
@Injectable()
export class UploadsFacadeService implements IUploadsFacadeService {

    /** The HTTP layer this service maps. */
    private readonly _api: IUploadsApiService = inject(UPLOADS_API);

    /** @inheritdoc */
    public getUploads(override: Partial<IDataTableRequest> = {}): Observable<IDataTableResponseModel<IUploadBatchModel>> {

        const request = AbstractUploadApiService.buildListRequest({
            page_size: UploadServiceConst.DEFAULT_PAGE_SIZE,
            sort_column: UploadServiceConst.DEFAULT_SORT_COLUMN,
            sort_type: DataTableSortEnum.DESC,
            ...override,
        });

        return this._api.getDataTable(request).pipe(

            // the counts are re-wrapped rather than read out, so the caller
            // still gets a response model and can page on it later
            map((response) => new DataTableResponseModel<IUploadBatchModel>({
                ...response.dto,
                data: response.data.map((dto) => new UploadBatchModel(dto)),
            })),
        );
    }

    /** @inheritdoc */
    public getBatch(batchId: string): Observable<IUploadBatchModel> {
        return this._api.getByBatchId(batchId).pipe(map((response) => new UploadBatchModel(response.data)));
    }

}
